import React from 'react';

import { useIntl } from 'react-intl';

import { useGetLinkedEntities } from 'src/features/pricingPlan/api/linkedEntities/useGetLinkedEntities';
import {
    LINKED_ENTITIES_KEYS,
    PricingPlanUsedInColumns,
    TransformedLinkedEntities,
    UsedInMembership,
    UsedInMembershipType,
} from 'src/features/pricingPlan/utils/types';
import { useParking } from 'src/providers/ParkingProvider';

const useTransformLinkedEntitiesResponse = ({
    pricingPlanId,
    querySortingOptions,
    queryFiltersOptions,
    querySearchOptions,
    enabled = true,
}: {
    pricingPlanId: string;
    querySortingOptions?: { ordering: string };
    queryFiltersOptions?: Record<string, string | string[]>;
    querySearchOptions?: Record<string, string>;
    // The linked entities are only needed while the consumer is actually showing them - a modal
    // that is still closed must not fetch them, because it is mounted once per table row.
    enabled?: boolean;
}): { data: TransformedLinkedEntities } => {
    const { locale } = useIntl();
    const { parkingId } = useParking();

    const { data, ...query } = useGetLinkedEntities({
        pricingPlanId,
        parkingId,
        ...querySortingOptions,
        ...queryFiltersOptions,
        ...querySearchOptions,
        config: { enabled: enabled && !!pricingPlanId },
    });

    const transformedData: PricingPlanUsedInColumns[] = React.useMemo(() => {
        if (!data) return [];

        const zones = data[LINKED_ENTITIES_KEYS.ZONE_PRICING_PLANS].zone_pricing_plans;
        const sites = data[LINKED_ENTITIES_KEYS.SITE_PRICING_PLANS].site_pricing_plans;
        const gateways = data[LINKED_ENTITIES_KEYS.GATEWAY_PRICING_PLANS].gateway_pricing_plans;
        const membership = data.memberships.memberships;
        const membership_types = data.membership_types.membership_types;

        return [
            ...zones.map((item) => ({
                ...item,
                id: item.zone.id,
                name: item.zone.translations?.find((translation) => translation.language === locale)?.name ?? '-',
                type: LINKED_ENTITIES_KEYS.ZONE_PRICING_PLANS,
            })),

            ...sites.map((item) => ({
                ...item,
                id: item.site.id,
                name: item.site.translations?.find((translation) => translation.language === locale)?.name ?? '-',
                type: LINKED_ENTITIES_KEYS.SITE_PRICING_PLANS,
            })),

            ...gateways.map((item) => ({
                ...item,
                id: item.gateway.id,
                name: item.gateway.translations?.find((translation) => translation.language === locale)?.name ?? '-',
                type: LINKED_ENTITIES_KEYS.GATEWAY_PRICING_PLANS,
            })),

            ...membership.map((item: UsedInMembership) => ({
                ...item,
                id: item.id,
                name: item.name ?? '-',
                type: LINKED_ENTITIES_KEYS.MEMBERSHIPS,
            })),

            ...membership_types.map((item: UsedInMembershipType) => ({
                ...item,
                id: item.id,
                name: item?.translations?.find((translation) => translation.language === locale)?.name ?? '-',
                type: LINKED_ENTITIES_KEYS.MEMBERSHIP_TYPES,
            })),
        ];
    }, [data, locale]);

    const totalActiveStays = transformedData.reduce((sum, item) => sum + (item.active_stays_count ?? 0), 0);
    const totalActiveMemberships = transformedData.reduce(
        (sum, item) => sum + (item?.active_memberships_count ?? 0),
        0,
    );

    return {
        ...query,
        data: {
            total_entities_count: data?.total_entities_count ?? 0,
            total_active_stays: totalActiveStays,
            total_active_memberships: totalActiveMemberships,
            transformedData: transformedData,
        },
    };
};

export default useTransformLinkedEntitiesResponse;
